import {Component, OnInit, ViewEncapsulation, ChangeDetectionStrategy, OnDestroy, ChangeDetectorRef} from '@angular/core';
import {AbstractComponent} from '../../../../formbird-sc-shared/src/lib/components/abstract.component';
import {ComponentDefinition} from '@formbird/types';
import {FormbirdInjectorService} from '@services/injector/formbird-injector.service';
import {ChangedDocumentService} from 'formbird-services';
import {ComponentSetupService} from '../../../../formbird-sc-shared/src/lib/component-setup.service';

type TimerDirection = 'up' | 'down';

interface TimerComponentDefinition extends ComponentDefinition {
  timerDirection?: TimerDirection;
  timerValue?: number;
  timerRepeat?: boolean;
  timerAutoStart?: boolean;
}

@Component({
  templateUrl: './sc-timer.component.html',
  styleUrls: ['./sc-timer.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ScTimerComponent extends AbstractComponent<TimerComponentDefinition> implements OnInit, OnDestroy {

  timeoutId?: number;
  remainingIntervalId?;
  msRemaining = 0;
  isTimerRunning = false;
  timerEndHitCount = 0;

  direction: TimerDirection = 'down';
  timerValue: number;
  timerAutoStart: boolean;
  timerRepeat?: boolean;

  constructor(
    protected formbirdInjectorService: FormbirdInjectorService,
    protected changedDocumentService: ChangedDocumentService,
    protected componentSetupService: ComponentSetupService,
    protected cdr: ChangeDetectorRef
  ) {
    super(formbirdInjectorService, changedDocumentService, componentSetupService, cdr);
  }


  startTimer(): any {
    this.isTimerRunning = true;
    const ms = this.timerValue * 1000;
    this.msRemaining = this.direction === 'down' ? ms : 0;
    this.cdr.detectChanges();

    this.remainingIntervalId = setInterval(() => {
      if (this.direction === 'down') {
        this.msRemaining -= 100;
      } else {
        this.msRemaining += 100;
      }
      this.cdr.detectChanges();
    }, 100);

    return setTimeout(() => {
      clearInterval(this.remainingIntervalId);
      this.timerEndHitCount += 1;

      // only emit if the timer is running
      // on stop press, we already emit the value so no need to also do it here
      if (this.isTimerRunning) {
        this.emitValue(false);
      }
      this.isTimerRunning = false;
      this.cdr.detectChanges();
      if (this.timerRepeat) {
        this.timeoutId = this.startTimer();
      }
    }, ms);
  }

  stopTimer(shouldAlsoEmit = false) {
    clearTimeout(this.timeoutId);
    clearInterval(this.remainingIntervalId);
    if (shouldAlsoEmit) {
      this.emitValue(true);
    }
    this.isTimerRunning = false;
    this.msRemaining = 0;
    this.cdr.detectChanges();
  }

  emitValue(wasStoppedManually: boolean) {
    let finalValue: number;
    if (this.timerRepeat) {
      finalValue = this.timerEndHitCount;
    } else {
      if (wasStoppedManually) {
        finalValue = this.msRemaining;
      } else {
        // we're dealing in ms
        const timerValue = this.timerValue * 1000;
        finalValue = this.direction === 'down' ? 0 : timerValue;
      }
    }
    console.log('emitting value', finalValue);
    this.valueChanged(finalValue);
  }

  ngOnInit() {
    super.ngOnInit();
    this.direction = this.componentDefinition.timerDirection ?? 'down';
    this.timerValue = this.componentDefinition.timerValue ?? 0;
    this.timerAutoStart = this.componentDefinition.timerAutoStart ?? false;
    this.timerRepeat = this.componentDefinition.timerRepeat ?? false;

    if (this.timerAutoStart) {
      this.timeoutId = this.startTimer();
    }
  }

  ngOnDestroy() {
    super.ngOnDestroy();
    this.stopTimer();
  }

  get displayText() {
    return this.isTimerRunning ? `${this.msRemaining}ms remaining` : 'Timer is not running';
  }
}
